/* Cart Drawer JavaScript - Cash for Games */

console.log('🚀 DEBUG: cart-drawer.js file is loading!');
console.log('🚀 DEBUG: Current time:', new Date().toLocaleTimeString());

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    console.log('CartDrawer: Constructor called for element:', this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    
    // Add overlay click listener if overlay exists
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        if (window.closeCartDrawerManual) {
          window.closeCartDrawerManual();
        } else {
          this.close();
        }
      });
    } else {
      console.warn('CartDrawer: Overlay not found');
    }

    // Add click-outside-to-close functionality
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    
    // Ensure open method is bound
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    
    console.log('CartDrawer: Constructor complete, open method type:', typeof this.open);
  }

  open(triggeredBy) {
    console.log('CartDrawer: Open method called', triggeredBy);
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // Prevent scrolling on body
    document.body.classList.add('overflow-hidden');
    this.classList.add('is-open');
    
    // Also add to the .cart-drawer element if it exists
    const drawerElement = this.querySelector('.cart-drawer');
    if (drawerElement) {
      drawerElement.classList.add('is-open');
    }
    
    console.log('CartDrawer: Added is-open class, classList:', this.classList);
    
    // Only initialize interactive elements, don't refresh on open
    this.initializeQuantityInputs();
    this.initializeRemoveButtons();
    
    // Focus trap
    this.trapFocus();

    // Add event listeners for click outside and escape key
    setTimeout(() => {
      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscapeKey);
    }, 100);
  }

  close() {
    console.log('CartDrawer: Close method called');
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    
    // Also remove from the .cart-drawer element if it exists
    const drawerElement = this.querySelector('.cart-drawer');
    if (drawerElement) {
      drawerElement.classList.remove('is-open');
    }
    
    // Remove event listeners
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleEscapeKey);
    
    // Simple focus return without complex trap removal
    if (this.activeElement && this.activeElement.focus) {
      this.activeElement.focus();
    }
  }

  handleOutsideClick(event) {
    // Check if click is outside the cart drawer
    const cartDrawerInner = this.querySelector('.cart-drawer__inner');
    if (cartDrawerInner && !cartDrawerInner.contains(event.target)) {
      this.close();
    }
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const elementToReplace = section.selector ? this.querySelector(section.selector) : this.querySelector(section.id);
      if (elementToReplace) {
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    // Update shipping progress
    this.updateShippingProgress(parsedState);
    
    // Re-initialize quantity inputs
    this.initializeQuantityInputs();
    this.initializeRemoveButtons();
  }

  async updateCartDrawer(cartData) {
    console.log('📦 DEBUG: updateCartDrawer called with cart data:', cartData);
    console.log('📦 DEBUG: Cart has', cartData.item_count, 'items');
    
    // Update cart count in header
    this.updateItemCount(cartData);
    
    // If cart is empty, show empty state
    if (cartData.item_count === 0) {
      console.log('📦 DEBUG: Cart is empty, showing empty state');
      this.showEmptyState();
      return;
    }
    
    // Rebuild cart drawer with fresh data
    console.log('📦 DEBUG: Building cart drawer HTML...');
    await this.buildCartDrawerHTML(cartData);
    
    console.log('📦 DEBUG: Cart drawer update complete');
  }

  async refreshCartDrawer() {
    try {
      // Get fresh cart data and update
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      await this.updateCartDrawer(cartData);
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }

  showEmptyState() {
    const cartDrawerInner = this.querySelector('.cart-drawer__inner');
    if (cartDrawerInner) {
      cartDrawerInner.innerHTML = `
        <div class="cart-drawer__header">
          <h2 class="cart-drawer__heading">Cart</h2>
          <button class="cart-drawer__close" type="button" onclick="closeCartDrawerManual()" aria-label="Close">
            <span class="cart-drawer__close-icon">&times;</span>
          </button>
        </div>
        <div class="cart-drawer__empty">
          <div class="cart-drawer__empty-content">
            <h3 class="cart-drawer__empty-title">Your cart is empty</h3>
            <a href="/collections/all" class="cart-drawer__empty-button" onclick="this.closest('cart-drawer').close()">
              Continue shopping
            </a>
          </div>
        </div>
      `;
    }
    console.log('Cart is empty, showing empty state');
  }

  generateDiscountHTML(cart) {
    if (!cart.cart_level_discount_applications || cart.cart_level_discount_applications.length === 0) {
      return '';
    }
    
    let discountHTML = '';
    cart.cart_level_discount_applications.forEach(discount => {
      discountHTML += `
        <div class="cart-drawer__discount">
          <span class="cart-drawer__totals-label">
            Discount: <span class="cart-drawer__discount-label">${discount.title}</span>
          </span>
          <span class="cart-drawer__totals-value cart-drawer__discount-value">
            -${window.Shopify.formatMoney(discount.total_allocated_amount)}
          </span>
        </div>
      `;
    });
    
    return discountHTML;
  }

  async buildCartDrawerHTML(cart) {
    const cartDrawerInner = this.querySelector('.cart-drawer__inner');
    if (!cartDrawerInner) return;

    // Calculate shipping progress
    const freeShippingThreshold = 6000;
    const remainingAmount = Math.max(0, freeShippingThreshold - cart.total_price);
    const progressPercentage = Math.min(100, (cart.total_price / freeShippingThreshold) * 100);

    let cartItemsHTML = '';
    cart.items.forEach((item, index) => {
      const lineIndex = index + 1;
      const imageUrl = item.image ? item.image.replace('.jpg', '_80x.jpg').replace('.png', '_80x.png') : '';
      
      cartItemsHTML += `
        <div class="cart-drawer__item" id="CartDrawer-Item-${lineIndex}">
          <div class="cart-drawer__item-image">
            ${item.image ? `<img src="${imageUrl}" alt="${item.title}" loading="lazy" width="80">` : ''}
          </div>
          <div class="cart-drawer__item-details">
            <div class="cart-drawer__item-info">
              <h3 class="cart-drawer__item-title">${item.title}</h3>
              <div class="cart-drawer__item-price">
                <span class="cart-drawer__item-price-current">${window.Shopify.formatMoney(item.price)}</span>
              </div>
            </div>
          </div>
          <div class="cart-drawer__item-controls">
            <div class="cart-drawer__item-quantity">
              <quantity-input class="quantity">
                <input class="quantity__input" type="number" data-quantity-variant-id="${item.variant_id}" name="updates[]" value="${item.quantity}" min="0" step="1" id="Drawer-quantity-${lineIndex}" data-index="${lineIndex}">
              </quantity-input>
            </div>
            <cart-remove-button id="CartDrawer-Remove-${lineIndex}" data-index="${lineIndex}">
              <button type="button" class="cart-drawer__remove-button" data-variant-id="${item.variant_id}">Remove</button>
            </cart-remove-button>
          </div>
        </div>
      `;
    });

    const shippingText = remainingAmount > 0 
      ? `Spend <strong>${window.Shopify.formatMoney(remainingAmount)}</strong> more and get <strong>free shipping!</strong>`
      : `<strong>You've earned free shipping!</strong>`;

    const drawerHTML = `
      <div class="cart-drawer__header">
        <h2 class="cart-drawer__heading">
          Cart
          <span class="cart-drawer__count">[${cart.item_count}]</span>
        </h2>
        <button class="cart-drawer__close" type="button" onclick="closeCartDrawerManual()">
          <span class="cart-drawer__close-icon">&times;</span>
        </button>
      </div>

      <div class="cart-drawer__content">
        <div class="cart-drawer__shipping-progress">
          <p class="cart-drawer__shipping-text ${remainingAmount <= 0 ? 'cart-drawer__shipping-text--success' : ''}">${shippingText}</p>
          <div class="cart-drawer__progress-bar">
            <div class="cart-drawer__progress-fill" style="width: ${progressPercentage}%; min-width: 10px; background: linear-gradient(90deg, #007aff 0%, #0066ff 100%);"></div>
          </div>
        </div>

        <cart-drawer-items>
          <form action="/cart" id="CartDrawer-Form" class="cart-drawer__form" method="post" onsubmit="return false;">
            <div id="CartDrawer-CartItems" class="cart-drawer__items">
              ${cartItemsHTML}
            </div>
          </form>
        </cart-drawer-items>
      </div>

      <div class="cart-drawer__footer">
        <div class="cart-drawer__totals">
          <div class="cart-drawer__subtotal">
            <span class="cart-drawer__totals-label">Subtotal</span>
            <span class="cart-drawer__totals-value">${window.Shopify.formatMoney(cart.original_total_price || cart.total_price)}</span>
          </div>
          ${this.generateDiscountHTML(cart)}
          <div class="cart-drawer__total">
            <span class="cart-drawer__totals-label cart-drawer__total-label">Total</span>
            <span class="cart-drawer__totals-value cart-drawer__total-value">${window.Shopify.formatMoney(cart.total_price)}</span>
          </div>
          <p class="cart-drawer__tax-note">Tax included and shipping calculated at checkout</p>
        </div>
        <div class="cart-drawer__actions">
          <a href="/cart" class="cart-drawer__view-cart" onclick="this.closest('cart-drawer').close()">View cart</a>
          <button type="button" class="cart-drawer__checkout" onclick="window.location.href='/checkout'">Checkout</button>
        </div>
      </div>
    `;

    cartDrawerInner.innerHTML = drawerHTML;
    
    // Re-initialize interactive elements
    this.initializeQuantityInputs();
    this.initializeRemoveButtons();
  }

  initializeRemoveButtons() {
    const removeButtons = this.querySelectorAll('.cart-drawer__remove-button');
    removeButtons.forEach(button => {
      // Remove existing listeners
      button.replaceWith(button.cloneNode(true));
      const newButton = this.querySelector(`[data-variant-id="${button.dataset.variantId}"]`);
      
      if (newButton) {
        newButton.addEventListener('click', async (e) => {
          e.preventDefault();
          const index = newButton.closest('cart-remove-button').dataset.index;
          await this.removeItem(index);
        });
      }
    });
  }

  async removeItem(lineIndex) {
    try {
      // Remove item using Shopify Cart API
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: lineIndex,
          quantity: 0
        })
      });

      if (response.ok) {
        // Get fresh cart data and update drawer
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();
        await this.updateCartDrawer(cartData);

        // Auto-close if empty
        if (cartData.item_count === 0) {
          setTimeout(() => this.close(), 500);
        }
      }
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  trapFocus() {
    // Simple focus on first focusable element
    const focusableElements = this.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const firstElement = focusableElements[0];
    
    if (firstElement) {
      firstElement.focus();
    }
  }

  updateCartContents() {
    fetch(`${routes.cart_url}.js`)
      .then(response => response.json())
      .then(cart => {
        this.updateItemCount(cart);
        this.updateShippingProgressFromCart(cart);
      })
      .catch(error => {
        console.error('Error updating cart contents:', error);
      });
  }

  updateItemCount(cart) {
    const countElement = this.querySelector('.cart-drawer__count');
    if (countElement) {
      countElement.textContent = cart.item_count > 0 ? `[${cart.item_count}]` : '';
    }
  }

  updateShippingProgressFromCart(cart) {
    const progressContainer = this.querySelector('.cart-drawer__shipping-progress');
    if (!progressContainer) {
      console.warn('Cart: Progress container not found');
      return;
    }

    const freeShippingThreshold = 6000; // $60.00 in cents
    const remainingAmount = Math.max(0, freeShippingThreshold - cart.total_price);
    const progressPercentage = Math.min(100, (cart.total_price / freeShippingThreshold) * 100);

    console.log('Cart: Progress debug:', {
      totalPrice: cart.total_price,
      threshold: freeShippingThreshold,
      percentage: progressPercentage,
      remaining: remainingAmount
    });

    const textElement = progressContainer.querySelector('.cart-drawer__shipping-text');
    const progressFill = progressContainer.querySelector('.cart-drawer__progress-fill');

    console.log('Cart: Progress elements found:', {
      textElement: !!textElement,
      progressFill: !!progressFill
    });

    if (textElement) {
      if (remainingAmount > 0) {
        textElement.innerHTML = `Spend <strong>${window.Shopify.formatMoney(remainingAmount)}</strong> more and get <strong>free shipping!</strong>`;
        textElement.classList.remove('cart-drawer__shipping-text--success');
      } else {
        textElement.innerHTML = '<strong>You\'ve earned free shipping!</strong>';
        textElement.classList.add('cart-drawer__shipping-text--success');
      }
    }

    if (progressFill) {
      progressFill.style.width = `${progressPercentage}%`;
      console.log('Cart: Set progress fill width to:', `${progressPercentage}%`);
      console.log('Cart: Progress fill element style:', progressFill.style.width);
    } else {
      console.error('Cart: Progress fill element not found');
    }
  }

  initializeQuantityInputs() {
    console.log('🔧 DEBUG: initializeQuantityInputs called');
    
    const quantityInputs = this.querySelectorAll('quantity-input');
    console.log('🔧 DEBUG: Found', quantityInputs.length, 'quantity inputs');
    
    quantityInputs.forEach((input, index) => {
      console.log('🔧 DEBUG: Initializing quantity input', index);
      
      if (!input.dataset.initialized) {
        try {
          // Check if QuantityInput class exists and has required elements
          const numberInput = input.querySelector('.quantity__input');
          if (numberInput) {
            console.log('🔧 DEBUG: Found number input, skipping QuantityInput class');
            // Don't use the QuantityInput class that's causing errors
            // new QuantityInput(input);
          }
          input.dataset.initialized = 'true';
        } catch (error) {
          console.log('🔧 DEBUG: Error initializing QuantityInput:', error);
        }
      }
      
      // Add enter key handler to prevent form submission
      const numberInput = input.querySelector('.quantity__input');
      if (numberInput) {
        console.log('🔧 DEBUG: Found number input, adding change listener');
        
        // Add direct change listener that triggers cart update
        numberInput.addEventListener('change', (e) => {
          console.log('🔧 DEBUG: Direct change event fired on input:', e.target.value);
          console.log('🔧 DEBUG: Input dataset index:', e.target.dataset.index);
          
          // Find the cart-drawer-items element and trigger quantity update
          const cartDrawerItems = e.target.closest('cart-drawer-items');
          if (cartDrawerItems) {
            console.log('🔧 DEBUG: Found cart-drawer-items, calling updateQuantity');
            cartDrawerItems.updateQuantity(e.target.dataset.index, e.target.value, e.target.name);
          } else {
            console.log('🔧 DEBUG: cart-drawer-items not found');
          }
        });
        
        numberInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            numberInput.blur(); // Remove focus to trigger change event
          }
        });
      } else {
        console.log('🔧 DEBUG: No number input found in quantity input');
      }
    });
  }
}

// Define the custom element immediately
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
  console.log('CartDrawer: Custom element defined');
} else {
  console.log('CartDrawer: Custom element already defined');
}

// Debug function to check if custom elements are working
window.debugCartDrawerElement = function() {
  const cartDrawer = document.querySelector('cart-drawer');
  console.log('DEBUG: Cart drawer element:', cartDrawer);
  console.log('DEBUG: Cart drawer constructor:', cartDrawer?.constructor);
  console.log('DEBUG: Cart drawer prototype:', Object.getPrototypeOf(cartDrawer));
  console.log('DEBUG: Has open method:', typeof cartDrawer?.open);
  console.log('DEBUG: All methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(cartDrawer)));
  
  if (cartDrawer && typeof cartDrawer.open !== 'function') {
    console.error('DEBUG: Cart drawer exists but open method is missing!');
    console.log('DEBUG: Manually adding open method...');
    
    // Manually attach the methods if they're missing
    const cartDrawerProto = CartDrawer.prototype;
    cartDrawer.open = cartDrawerProto.open.bind(cartDrawer);
    cartDrawer.close = cartDrawerProto.close.bind(cartDrawer);
    
    console.log('DEBUG: After manual attachment, open method type:', typeof cartDrawer.open);
  }
  
  return cartDrawer;
};

class CartDrawerItems extends HTMLElement {
  constructor() {
    super();
    console.log('🏗️ DEBUG: CartDrawerItems constructor called');
    
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    console.log('🏗️ DEBUG: Current item count:', this.currentItemCount);

    this.debouncedOnChange = debounce((event) => {
      console.log('🏗️ DEBUG: Debounced onChange called');
      this.onChange(event);
    }, 50);

    console.log('🏗️ DEBUG: Adding change event listener to CartDrawerItems');
    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  onChange(event) {
    console.log('🎯 DEBUG: onChange triggered');
    console.log('🎯 DEBUG: Event target:', event.target);
    console.log('🎯 DEBUG: Dataset index:', event.target.dataset.index);
    console.log('🎯 DEBUG: New value:', event.target.value);
    
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  async updateQuantity(line, quantity, name) {
    console.log('🔄 DEBUG: updateQuantity called with:', { line, quantity, name });
    
    try {
      console.log('🔄 DEBUG: Making request to /cart/change.js');
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });

      console.log('🔄 DEBUG: Change response status:', response.status);

      if (response.ok) {
        console.log('🔄 DEBUG: Getting fresh cart data...');
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();
        
        console.log('🔄 DEBUG: Fresh cart data:', cartData);
        console.log('🔄 DEBUG: Item count:', cartData.item_count);
        
        // Update the entire cart drawer with fresh data
        console.log('🔄 DEBUG: Calling updateCartDrawer...');
        await this.closest('cart-drawer').updateCartDrawer(cartData);
        console.log('🔄 DEBUG: updateCartDrawer completed');
        
        // Also trigger cart page updates if on cart page
        const cartPageEvent = new CustomEvent('cart:updated', { detail: cartData });
        document.dispatchEvent(cartPageEvent);
      } else {
        console.error('🔄 DEBUG: Change request failed:', response.status);
      }
    } catch (error) {
      console.error('🔄 DEBUG: Error in updateQuantity:', error);
    }
  }

  enableLoading(line) {
    const item = this.querySelector(`#CartDrawer-Item-${line}`);
    if (item) item.classList.add('cart-item--loading');
  }

  disableLoading(line) {
    const item = this.querySelector(`#CartDrawer-Item-${line}`);
    if (item) item.classList.remove('cart-item--loading');
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-drawer-items') || this.closest('cart-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

// Note: QuantityInput is defined in global.js

// Utility functions
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

// Simple add to cart function
async function addToCart(variantId, quantity = 1) {
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    });

    if (response.ok) {
      // Open cart drawer and refresh with latest data
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.open();
        await cartDrawer.refreshCartDrawer();
      }
    }
  } catch (error) {
    console.error('Error adding to cart:', error);
  }
}

// Simple form interception
function interceptAddToCartForms() {
  document.addEventListener('submit', function(e) {
    const form = e.target;
    if (form && form.action && form.action.includes('/cart/add')) {
      e.preventDefault();
      
      const formData = new FormData(form);
      const variantId = formData.get('id');
      const quantity = formData.get('quantity') || 1;
      
      if (variantId) {
        addToCart(parseInt(variantId), parseInt(quantity));
      }
    }
  });
}

// Initialize cart functionality
document.addEventListener('DOMContentLoaded', function() {
  console.log('🚀 DEBUG: DOMContentLoaded fired - cart functionality initializing');
  console.log('🚀 DEBUG: Looking for cart-drawer element...');
  
  const cartDrawer = document.querySelector('cart-drawer');
  console.log('🚀 DEBUG: Found cart-drawer:', !!cartDrawer);
  
  if (cartDrawer) {
    console.log('🚀 DEBUG: Cart drawer found, type:', typeof cartDrawer);
    console.log('🚀 DEBUG: Cart drawer methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(cartDrawer)));
  }
  
  // Prevent cart drawer form submissions
  document.addEventListener('submit', function(e) {
    const form = e.target;
    if (form && form.id === 'CartDrawer-Form') {
      console.log('🚀 DEBUG: Preventing CartDrawer-Form submission');
      e.preventDefault();
      return false;
    }
  });
  
  // Start intercepting add to cart forms
  interceptAddToCartForms();
});


// Initialize cart drawer functionality
document.addEventListener('DOMContentLoaded', () => {
  // Update money formatting
  if (window.Shopify && window.Shopify.formatMoney) {
    // Money formatting is already available
  } else {
    window.Shopify = window.Shopify || {};
    window.Shopify.formatMoney = function(cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || '${{amount}}';

      function defaultTo(value, defaultValue) {
        return value == null || value !== value ? defaultValue : value;
      }

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultTo(precision, 2);
        thousands = defaultTo(thousands, ',');
        decimal = defaultTo(decimal, '.');

        if (isNaN(number) || number == null) return 0;

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount;
      }

      const value_formatted = formatWithDelimiters(cents, 2);
      return formatString.replace(placeholderRegex, value_formatted);
    };
  }
});